import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from '../ui/Button';
import { formatFullDateTime, getTomorrowDateString } from '../../utils/dateUtils';

const InterviewRescheduleModal = ({ interview, onClose, onReschedule }) => {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentDateTime = useMemo(() => {
    return interview ? formatFullDateTime(interview.scheduledTime) : '';
  }, [interview]);

  useEffect(() => {
    if (interview) {
      // Set default to tomorrow
      const tomorrowStr = getTomorrowDateString();
      
      setFormData({
        date: tomorrowStr,
        time: '10:00',
        reason: ''
      });
      setError(null);
    }
  }, [interview]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validation
      if (!formData.date || !formData.time) {
        throw new Error('Date and time are required');
      }

      const scheduledDateTime = new Date(`${formData.date}T${formData.time}`);
      const now = new Date();
      
      if (scheduledDateTime <= now) {
        throw new Error('Interview must be scheduled for a future date and time');
      }

      // Check if it's at least 1 hour from now
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
      if (scheduledDateTime < oneHourFromNow) {
        throw new Error('Interview must be scheduled at least 1 hour from now');
      }

      await onReschedule(interview.id, scheduledDateTime.toISOString(), formData.reason);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [formData, interview?.id, onReschedule, onClose]);

  if (!interview) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Reschedule Interview</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-gray-400 text-xl">×</span>
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current Interview Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">{interview.jobTitle}</h3>
            <p className="text-sm text-gray-600 mb-2">
              Currently scheduled for: <span className="font-medium">{currentDateTime}</span>
            </p>
            <p className="text-sm text-gray-600">
              Duration: {interview.duration} minutes • Type: {interview.type}
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* New Date and Time */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Time
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rescheduling (Optional)
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Brief explanation for the reschedule..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                disabled={loading}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Rescheduling...' : 'Reschedule Interview'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterviewRescheduleModal;
